import React from 'react';

import {Box} from '@material-ui/core';
import {useHistory} from 'react-router';

import {useFlowFieldSearch, useSpinner} from 'hooks';
import {useSnackbar} from 'notistack';
import {EfeAccordion} from 'components';
import {Constants, HistoryRoutes, Messages, SnackbarVariants, ViewKeys} from 'commons/constants';
import {MainContent} from 'layouts/commons';
import {getBase64Extension, nextStep} from 'commons/utils/Functions';
import AddressProofForm from './address-proof-form/AddressProofForm';
import {
    AddressProofValues,
    Documents,
    EfeAccordionValues,
    EfeResponse,
    Field,
    UploadDocumentServiceRequest,
} from 'commons/models';
import {useFlowContext} from 'context/onboarding-flow/OnboardingFlow';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {ReactComponent as ActionIcon} from 'assets/icons/section/action/edit.svg';
import {DocumentServices} from 'services';

function AddressProof() {
    const history = useHistory();
    const {flow, updateModuleStep} = useFlowContext();
    const {expedientId, fatherKycId, menu} = useMenuContext();
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const {enqueueSnackbar} = useSnackbar();
    const [fields, setFields] = React.useState<Array<Field>>(useFlowFieldSearch());
    const [addressProof, setAddressProf] = React.useState<any>(undefined);

    React.useEffect(() => {
        if (fatherKycId > 0 && ViewKeys.FISCAL_LOCATION_ADDRESS === flow.data.expandKey) {
            setIsLoading(true);
            (async function () {
                try {
                    const response: EfeResponse<Documents> =
                        await DocumentServices.getDocumentByKycId(
                            Constants.DOC_COMP_DOMICILIO,
                            expedientId,
                            fatherKycId
                        );
                    if (response && response.data) {
                        setAddressProf(response.data.data);
                    }
                    setIsLoading(false);
                } catch (error) {
                    setIsLoading(false);
                }
            })();
        }
    }, []);

    async function onSubmit(values: AddressProofValues) {
        setIsLoading(true);
        try {
            const extension = getBase64Extension(values.file);
            const fileConverted = await (await fetch(values.file)).blob();
            const request: UploadDocumentServiceRequest = {
                documentKey: Constants.DOC_COMP_DOMICILIO,
                expedientId: expedientId,
                file: fileConverted,
                kycId: fatherKycId,
                filename: `${Constants.DOC_COMP_DOMICILIO}.${extension}`,
            };

            const response: EfeResponse<any> = await DocumentServices.updateDocument(request);
            if (response && response.data) {
                enqueueSnackbar(Messages.FILE_UPLOAD_OK, {
                    variant: SnackbarVariants.SUCCESS,
                });
                const path = nextStep(flow, menu);
                if (Constants.MODULE_STEP !== path) {
                    setIsLoading(false);
                    history.push(path);
                } else {
                    updateModuleStep(true);
                    flow.data.expandKey = path;
                    setIsLoading(false);
                }
            } else {
                setFields([...fields]);
                setIsLoading(false);
            }
        } catch (error) {
            setIsLoading(false);
            setFields([...fields]);
            enqueueSnackbar(Messages.FILE_NOT_UPLOAD, {
                variant: SnackbarVariants.ERROR,
            });
        }
    }

    const accordion: Array<EfeAccordionValues> = [
        {
            icon: <ActionIcon />,
            title: 'Comprobante de Domicilio',
            reactElement: (
                <Box mt={8}>
                    {!isLoading && (
                        <AddressProofForm
                            fields={fields}
                            // eslint-disable-next-line react/jsx-handler-names
                            submitHandler={onSubmit}
                            addressProof={addressProof}
                        />
                    )}
                </Box>
            ),
            expandIcon: true,
            disabled: false,
            expanded: ViewKeys.FISCAL_LOCATION_ADDRESS === flow.data.expandKey,
        },
    ];

    return (
        <MainContent>
            {isLoading && useSpinner(isLoading)}

            <EfeAccordion
                onChange={() => history.push(HistoryRoutes.ADDRESS_PROOF)}
                values={accordion}
            />
        </MainContent>
    );
}

export default AddressProof;
